<!-- =========================================================================================
  File Name: CustomerDebtListFormSidebar.vue
  Description: Add New Data - Sidebar component
========================================================================================== -->
<i18n src="@/locales/modules/CustomerTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="
			setDialogHide =>
				handleCloseDialog(() => {
					initValues();
					setDialogHide();
				}, isFormDirty)
		"
		class="custom-dialog todo-list-form-sidebar"
		:visible.sync="isSidebarActiveLocal"
	>
		<div slot="title">
			<h4 class="uppercase font-bold block text-xl w-full">{{ !kcsItem.id ? $t("Actions.Create") : $t("Actions.Edit") }} {{ $t("ImportProduct") }}</h4>
		</div>
		<perfect-scrollbar ref="vueScrollBar" class="scroll-area--data-list" :settings="settings">
			<div class="p-6">
				<!-- Select Product -->
				<div class="mb-4">
					<label for class="text-base vs-input--label"> {{ $t("Products") }}<span class="text-danger">*</span>: </label>
					<v-select
						:disabled="!!kcsItem.id && product && !!product.id"
						:clearable="false"
						class="border-none w-full"
						:placeholder="`${$t('Actions.Choose')} ${$t('Products').toLowerCase()}`"
						v-model="product"
						name="product"
						:options="productOptions"
						label="name"
						:data-vv-as="$t('Products')"
						v-validate="'required'"
					/>
					<span class="text-danger text-sm break-normal" v-show="errors.has('product')">{{ errors.first("product") }}</span>
				</div>

				<!-- select inventory -->
				<div class="mb-4">
					<label for class="text-base vs-input--label"> {{ $t("Inventory") }}<span class="text-danger">*</span>: </label>
					<v-select
						:clearable="false"
						class="border-none w-full"
						:placeholder="`${$t('Actions.Choose')} ${$t('Inventory').toLowerCase()}`"
						v-model="inventory"
						name="inventory"
						:options="inventoryOptions"
						label="name"
						:data-vv-as="$t('Inventory')"
						v-validate="'required'"
					/>
					<span class="text-danger text-sm break-normal" v-show="errors.has('inventory')">{{ errors.first("inventory") }}</span>
				</div>
				<!-- Location -->
				<div class="mb-4">
					<label for class="vs-input--label">{{ $t("Location") }}<span class="text-danger">*</span>:</label>
					<cbs-input v-model="bin" class="w-full" name="bin" :placeholder="`${$t('Actions.Input')} ${$t('Location').toLowerCase()}`" v-validate="'required'" :data-vv-as="$t('Location')" />
					<span class="text-danger text-sm break-normal" v-show="errors.has('bin')">{{ errors.first("bin") }}</span>
				</div>

				<!-- imported date-->
				<div class="mb-4 grid grid-cols-2 gap-2">
					<div>
						<label for class="vs-input--label">{{ $t("ImportedAt") }}<span class="text-danger">*</span>:</label>

						<vx-input-group>
							<template slot="append">
								<div class="append-text text-black">
									<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
								</div>
							</template>

							<cbs-date-picker
								clearable
								class="w-full"
								type="datetime"
								name="imported_at"
								prefix-icon="''"
								ref="dateTimePickerRef"
								format="DD-MM-yyyy HH:mm"
								value-format="yyyy-MM-dd HH:mm:ss"
								:data-vv-as="$t('ImportedAt')"
								:placeholder="`${$t('Actions.ChooseDate')}`"
								v-model="imported_at"
								v-validate="'required'"
							/>
						</vx-input-group>
						<span class="text-danger text-sm break-normal" v-show="errors.has('imported_at')">{{ errors.first("imported_at") }}</span>
					</div>
					<div>
						<label for class="vs-input--label">{{ $t("ImportedTo") }}:</label>

						<vx-input-group>
							<template slot="append">
								<div class="append-text text-black">
									<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
								</div>
							</template>

							<cbs-date-picker
								clearable
								class="w-full"
								type="datetime"
								name="imported_to"
								prefix-icon="''"
								ref="dateTimePickerRef"
								format="DD-MM-yyyy HH:mm"
								value-format="yyyy-MM-dd HH:mm:ss"
								:data-vv-as="$t('ImportedTo')"
								:placeholder="`${$t('Actions.ChooseDate')}`"
								v-model="imported_to"
							/>
						</vx-input-group>
						<span class="text-danger text-sm break-normal" v-show="errors.has('imported_to')">{{ errors.first("imported_to") }}</span>
					</div>
				</div>

				<!-- LotNumber -->
				<div class="manufacture-year">
					<label for class="vs-input--label">{{ $t("LotNumber") }}<span class="text-danger">*</span>:</label>
					<cbs-input v-model="lot_number" class="w-full" name="lot_number" :placeholder="`${$t('Actions.Input')} ${$t('LotNumber')}`" v-validate="'required'" :data-vv-as="$t('LotNumber')" />
					<span class="text-danger text-sm break-normal" v-show="errors.has('lot_number')">{{ errors.first("lot_number") }}</span>
				</div>

				<!-- Numbers -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Numbers") }}<span class="text-danger">*</span>:</label>
					<vx-input-group prependClasses="border border-solid border-grey border-r-0">
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="quantity"
							name="quantity"
							:userSetting="userSettings"
							v-validate="{ min_value: 0, required: true }"
							:data-vv-as="$t('Numbers')"
							:placeholder="`${$t('Actions.Input')} ${$t('Numbers').toLowerCase()}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('quantity')">{{ errors.first("quantity") }}</span>
				</div>
				<!-- Exp -->
				<div class="deadline my-5">
					<label for class="vs-input--label">{{ $t("ExpiredDays") }}<span class="text-danger">*</span>:</label>

					<vx-input-group>
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="expiredDays"
							name="exp-date"
							:userSetting="userSettings"
							v-validate="{ min_value: 0, required: true }"
							:data-vv-as="$t('ExpiredDays')"
							:placeholder="`${$t('Actions.InputNumberOfDays')}`"
						/>
					</vx-input-group>
					<p class="italic">
						Hạn sử dụng là số ngày tính từ ngày nhập
					</p>
					<span class="text-danger text-sm break-normal" v-show="errors.has('exp-date')">{{ errors.first("exp-date") }}</span>
				</div>
			</div>
		</perfect-scrollbar>

		<div class="flex justify-center flex-wrap items-center" slot="footer">
			<vs-button class="w-full sm:w-1/3 font-bold" @click.stop="submitData(true)">{{ $t("Actions.Save") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import _ from "lodash";
import vSelect from "vue-select";
import CbsEditor from "@/components/cbs-editor/CbsEditor";
import riceInventoryGql from "@/http/requests/rice/inventory_gql";
import customerCardGql from "@/http/requests/card/customer_gql";

export default {
	name: "KSCFormSideBar",
	provide() {
		return { parentValidator: this.$validator };
	},
	props: {
		kcsItem: {
			type: Object,
			default: () => {}
		},
		isSidebarActive: {
			type: Boolean,
			required: true
		}
	},
	components: {
		vSelect,
		CbsEditor
	},
	data() {
		return {
			lot_number: "",
			product: "",
			inventory: "",
			imported_at: "",
			imported_to: "",
			bin: "",
			isFormDirty: false,
			quantity: null,
			expiredDays: null,

			productOptions: [],
			inventoryOptions: [],

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			}
		};
	},
	watch: {
		isSidebarActive(val) {
			if (!val) {
				return;
			}
			if (!this.kcsItem.id) {
				this.initValues();
			} else {
				const { lot_number, product, inventory, quantity, imported_at, expired_days, imported_to, bin } = this.kcsItem;
				this.lot_number = lot_number;
				this.product = product;
				this.inventory = inventory;
				this.quantity = quantity;
				this.imported_at = imported_at;
				this.expiredDays = expired_days;
				this.imported_to = imported_to;
				this.bin = bin;
			}

			this.isFormDirty = false;
			this.initFormData = {
				lot_number: this.lot_number,
				product: this.product,
				inventory: this.inventory,
				quantity: this.quantity,
				imported_at: this.imported_at,
				expiredDays: this.expiredDays,
				imported_to: this.imported_to,
				bin: this.bin
			};
			this.getOptions();
		},
		data: function(newVal, oldVal) {
			if (_.isEmpty(newVal) || newVal.id !== oldVal.id) {
				this.$nextTick(() => {
					if (this.$refs.vueScrollBar) {
						this.$refs.vueScrollBar.$el.scrollTop = 0;
					}
				});
			}
		},
		computedFormData: {
			handler: function(newVal) {
				this.isFormDirty = JSON.stringify(newVal) !== JSON.stringify(this.initFormData);
			},
			deep: true
		}
	},

	computed: {
		isSidebarActiveLocal: {
			get() {
				return this.isSidebarActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeSidebar");
				}
			}
		},
		computedFormData() {
			return {
				lot_number: this.lot_number,
				product: this.product,
				inventory: this.inventory,
				quantity: this.quantity,
				imported_at: this.imported_at,
				expiredDays: this.expiredDays,
				imported_to: this.imported_to,
				bin: this.bin
			};
		}
	},
	methods: {
		initValues() {
			this.lot_number = "";
			this.product = "";
			this.inventory = "";
			this.imported_at = "";
			this.quantity = "";
			this.expiredDays = "";
			this.bin = "";
			this.imported_to = "";
			this.isFormDirty = false;
			this.setInitFormData();
			this.$validator.reset();
		},
		setInitFormData() {
			this.$nextTick(() => {
				this.initFormData = {
					lot_number: this.lot_number,
					product: this.product,
					inventory: this.inventory,
					quantity: this.quantity,
					imported_at: this.imported_at,
					expiredDays: this.expiredDays,
					imported_to: this.imported_to,
					bin: this.bin
				};
			});
		},
		handleCreateOrUpdateSuccess(isCloseForm) {
			if (isCloseForm) {
				this.$emit("closeSidebar");
			}
			this.initValues();
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: `${!this.kcsItem.id ? this.$t("Actions.Create") : this.$t("Actions.Edit")} KCS ${this.$t("Successful").toLowerCase()} `
				}
			});
		},
		async handleCreateOrUpdateKSC(dataObj, isCloseForm) {
			try {
				this.$vs.loading();
				try {
					if (dataObj.id) {
						await this.$store.dispatch("inventoryManagement/updateKCS", dataObj);
					} else {
						await this.$store.dispatch("inventoryManagement/addKCS", dataObj);
					}
					this.handleCreateOrUpdateSuccess(isCloseForm);
				} catch (error) {
					this.$helper.notifyError({ context: this, error });
					console.error("HH", error);
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		submitData(isCloseForm = false) {
			this.$validator.validateAll().then(result => {
				if (result) {
					try {
						let dataObj = {
							id: this.kcsItem.id,
							product_id: this.product.id,
							inventory_id: this.inventory.id,
							lot_number: this.lot_number,
							quantity: this.quantity,
							imported_at: this.imported_at,
							expired_days: this.expiredDays,
							imported_to: this.imported_to,
							bin: this.bin
						};

						console.log("Submit ", dataObj);
						this.handleCreateOrUpdateKSC(dataObj, isCloseForm);
					} catch (error) {
						console.error(error);
					}
				}
			});
		},
		triggerClickDatetimePicker() {
			this.$refs.dateTimePickerRef.$children[0].pickerVisible = true;
		},
		async getOptions() {
			this.$vs.loading();
			try {
				const { inventories } = await riceInventoryGql.fetchInventories({});
				this.inventoryOptions = inventories;
				const { products } = await customerCardGql.fetchShowProducts({});
				this.productOptions = products;
			} catch (error) {
				console.error(error);
			} finally {
				this.$vs.loading.close();
			}
		}
	},
	created() {}
};
</script>
<style lang="scss" scoped></style>
